import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAppointments,
  assignAppointment,
  rejectAppointment,
  completeAppointment,
  printPaymentReceipt,
  fetchDoctorsByDepartment,
} from "../../Redux/actions/receptionistActions/receptionistActions";
import { showToast } from "../../components/ToastMessage";
import { printWalkInReceipt } from "../../components/receptionist/PrintWalkInReceipt";
import { printPaymentReceipt as printPayment } from "../../components/adminComponents/Appointments/PrintPaymentReceipt";
import Loading from "../../components/Loading";
import API from "../../api";
import {
  Bell,
  Calendar,
  Clock,
  Users,
  CheckCircle,
  XCircle,
  FileText,
  Eye,
  UserCheck,
  DollarSign,
  Printer,
  Receipt,
} from "lucide-react";

// Import Modal Components
import AssignDoctorModal from "../../components/receptionist/AssignDoctorModal";
import RejectAppointmentModal from "../../components/receptionist/RejectAppointmentModal";
import PaymentModal from "../../components/receptionist/PaymentModal";
import ViewAppointmentModal from "../../components/receptionist/ViewAppointmentModal";
import VitalsModal from "../../components/receptionist/VitalsModal";
import { useSanitizedInput } from "../../hooks/useSanitizedInput";

// Convert time to 12-hour format
const formatTime12Hour = (time24) => {
  if (!time24) return "-";
  const [hours, minutes] = time24.split(":");
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

const getToday = () => {
  const t = new Date();
  const yyyy = t.getFullYear();
  const mm = String(t.getMonth() + 1).padStart(2, "0");
  const dd = String(t.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const getMonthRange = () => {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const formatDate = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  return { start: formatDate(firstDay), end: formatDate(lastDay) };
};

const ManageAppointmentsReceptionist = () => {
  const dispatch = useDispatch();
  const { appointments, loading, doctors } = useSelector(
    (state) => state.receptionist
  );

  const [activeTab, setActiveTab] = useState("pending");
  const [filters, setFilters] = useState({
    date: "",
    department: "",
    search: "",
  });

  const [departments, setDepartments] = useState([]);
  const [searchTerm, handleSearchChange, resetSearch] = useSanitizedInput(
    "",
    "text"
  );
  const [newAppointmentsCount, setNewAppointmentsCount] = useState(0);

  // Modal states
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showVitalsModal, setShowVitalsModal] = useState(false);

  // Form states
  const [assignData, setAssignData] = useState({
    doctorId: "",
    date: getToday(),
    time: "",
    priority: "Normal",
    waitingTime: 30,
  });
  const [rejectReason, setRejectReason] = useState("");
  const [paymentData, setPaymentData] = useState({
    feePaid: "",
    method: "cash",
  });
  const [vitalsData, setVitalsData] = useState({
    bp: "",
    weight: "",
    spo2: "",
    temperature: "",
    height: "",
    bmi: "",
    addNotes: "",
  });

  // Fetch departments on mount
  useEffect(() => {
    API.get("/receptionist/departments")
      .then((res) => setDepartments(res.data || []))
      .catch((err) => console.error(err));
  }, []);

  // Debounced search
  useEffect(() => {
    const delay = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchTerm }));
    }, 500);
    return () => clearTimeout(delay);
  }, [searchTerm]);

  // Fetch appointments based on active tab and filters
  useEffect(() => {
    const fetchData = () => {
      let queryFilters = { ...filters };

      if (activeTab === "pending") {
        queryFilters.status = "pending";
      } else if (activeTab === "confirmed") {
        queryFilters.status = "confirmed";
      } else if (activeTab === "completed") {
        queryFilters.status = "completed";
      } else if (activeTab === "rejected") {
        queryFilters.status = "rejected";
      } else if (activeTab === "today") {
        queryFilters.status = "confirmed";
        queryFilters.date = getToday();
      } else if (activeTab === "upcoming") {
        const { start, end } = getMonthRange();
        queryFilters.status = "confirmed";
        queryFilters.date = start;
      }

      const cleanFilters = Object.fromEntries(
        Object.entries(queryFilters).filter(([_, v]) => v !== "")
      );

      dispatch(fetchAppointments(cleanFilters));
    };

    fetchData();

    const intervalId = setInterval(fetchData, 30000);

    return () => clearInterval(intervalId);
  }, [dispatch, activeTab, filters]);

  // Check for new pending appointments
  useEffect(() => {
    if (appointments && activeTab === "pending") {
      setNewAppointmentsCount(appointments.length);
    }
  }, [appointments, activeTab]);

  const refreshAppointments = () => {
    let queryFilters = { ...filters };

    if (activeTab === "pending") {
      queryFilters.status = "pending";
    } else if (activeTab === "confirmed") {
      queryFilters.status = "confirmed";
    } else if (activeTab === "completed") {
      queryFilters.status = "completed";
    } else if (activeTab === "rejected") {
      queryFilters.status = "rejected";
    } else if (activeTab === "today") {
      queryFilters.status = "confirmed";
      queryFilters.date = getToday();
    }

    const cleanFilters = Object.fromEntries(
      Object.entries(queryFilters).filter(([_, v]) => v !== "")
    );
    dispatch(fetchAppointments(cleanFilters));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      date: "",
      department: "",
      search: "",
    });
    resetSearch();
  };

  const handleAssignClick = (appt) => {
    setSelectedAppt(appt);
    setAssignData({
      doctorId: "",
      date: getToday(),
      time: "",
      priority: appt.priority || "Normal",
      waitingTime: appt.waitingTime || 30,
    });

    // ✅ FIXED: Prefill vitals from current appointment OR last vitals
    const vitalsSource = appt.vitals || appt.lastVitals || {};

    setVitalsData({
      bp: vitalsSource.bp || "",
      weight: vitalsSource.weight || "",
      spo2: vitalsSource.spo2 || "",
      temperature: vitalsSource.temperature || "",
      height: vitalsSource.height || "",
      bmi: vitalsSource.bmi || "",
      addNotes: vitalsSource.addNotes || "",
    });

    if (appt.department) {
      dispatch(fetchDoctorsByDepartment(appt.department));
    }

    setShowAssignModal(true);
  };

  const handleAssignModalNext = () => {
    if (!assignData.doctorId || !assignData.date || !assignData.time) {
      showToast("All fields required");
      return;
    }

    // Close assign modal and open vitals modal
    setShowAssignModal(false);
    setShowVitalsModal(true);
  };

  const handleVitalsSubmit = async () => {
    // Combine assign data with vitals data
    const fullData = {
      ...assignData,
      bp: vitalsData.bp,
      weight: vitalsData.weight,
      spo2: vitalsData.spo2,
      temperature: vitalsData.temperature,
      height: vitalsData.height,
      bmi: vitalsData.bmi,
      addNotes: vitalsData.addNotes,
    };

    const res = await dispatch(assignAppointment(selectedAppt._id, fullData));
    if (res.ok) {
      showToast("Appointment confirmed with vitals recorded");
      setShowVitalsModal(false);
      refreshAppointments();
    } else {
      showToast(res.error);
    }
  };

  const handleRejectClick = (appt) => {
    setSelectedAppt(appt);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const handleRejectSubmit = async () => {
    const res = await dispatch(
      rejectAppointment(selectedAppt._id, rejectReason)
    );
    if (res.ok) {
      showToast("Appointment rejected");
      setShowRejectModal(false);
      refreshAppointments();
    } else {
      showToast(res.error);
    }
  };

  const handleComplete = async (appt) => {
    if (window.confirm("Mark this appointment as completed?")) {
      const res = await dispatch(completeAppointment(appt._id));
      if (res.ok) {
        showToast("Appointment completed");
        refreshAppointments();
      } else {
        showToast(res.error);
      }
    }
  };

  const handlePaymentClick = (appt) => {
    setSelectedAppt(appt);
    setPaymentData({
      feePaid: appt.payment?.feePaid || "",
      method: appt.payment?.method || "cash",
    });
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = async () => {
    if (!paymentData.feePaid || paymentData.feePaid <= 0) {
      showToast("Enter valid amount");
      return;
    }

    const res = await dispatch(
      printPaymentReceipt(selectedAppt._id, paymentData)
    );
    if (res.ok) {
      showToast("Payment recorded successfully");
      printPayment(res.appointment);
      setShowPaymentModal(false);
      refreshAppointments();
    } else {
      showToast(res.error);
    }
  };

  const handleViewClick = (appt) => {
    setSelectedAppt(appt);
    setShowViewModal(true);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: "#ffc107", text: "#000" },
      confirmed: { bg: "#28a745", text: "#fff" },
      rejected: { bg: "#dc3545", text: "#fff" },
      completed: { bg: "#6c757d", text: "#fff" },
    };
    return badges[status] || badges.pending;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      "Most Urgent": "#dc3545",
      Urgent: "#ffc107",
      Normal: "#17a2b8",
    };
    return colors[priority] || colors.Normal;
  };

  const filteredAppointments = appointments || [];

  const tabConfig = [
    {
      id: "pending",
      label: "Pending",
      icon: Clock,
      color: "#ffc107",
      showNotification: true,
    },
    {
      id: "confirmed",
      label: "Confirmed",
      icon: CheckCircle,
      color: "#28a745",
    },
    {
      id: "completed",
      label: "Completed",
      icon: FileText,
      color: "#6c757d",
    },
    {
      id: "rejected",
      label: "Rejected",
      icon: XCircle,
      color: "#dc3545",
    },
    {
      id: "all",
      label: "All Appointments",
      icon: Users,
      color: "#667eea",
    },
  ];

  return (
    <div style={{ background: "#f8f9fa", minHeight: "100vh", padding: "2rem" }}>
      <div className="container-fluid">
        {/* Quick Action Buttons */}
        <div className="row mb-4">
          <div className="col-md-4">
            <div
              className="card"
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                cursor: "pointer",
                transition: "transform 0.2s",
              }}
              onClick={() => setActiveTab("today")}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = "translateY(-4px)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "translateY(0)")
              }
            >
              <div className="card-body text-center p-4">
                <Calendar size={40} color="#667eea" className="mb-2" />
                <h5 className="mb-1">Today's Appointments</h5>
                <p className="text-muted mb-0 small">
                  View all confirmed appointments for today
                </p>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div
              className="card"
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                cursor: "pointer",
                transition: "transform 0.2s",
              }}
              onClick={() => setActiveTab("upcoming")}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = "translateY(-4px)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "translateY(0)")
              }
            >
              <div className="card-body text-center p-4">
                <Clock size={40} color="#28a745" className="mb-2" />
                <h5 className="mb-1">Upcoming This Month</h5>
                <p className="text-muted mb-0 small">
                  View all appointments for this month
                </p>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div
              className="card"
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                cursor: "pointer",
                transition: "transform 0.2s",
              }}
              onClick={() => {
                setActiveTab("pending");
                setNewAppointmentsCount(0);
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = "translateY(-4px)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "translateY(0)")
              }
            >
              <div className="card-body text-center p-4 position-relative">
                <Bell size={40} color="#ffc107" className="mb-2" />
                {newAppointmentsCount > 0 && (
                  <span className="position-absolute top-0 end-0 badge rounded-pill bg-danger m-3">
                    {newAppointmentsCount}
                  </span>
                )}
                <h5 className="mb-1">New Requests</h5>
                <p className="text-muted mb-0 small">
                  Check pending appointment requests
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="row mb-4">
          <div className="col-12">
            <div
              className="card"
              style={{
                borderRadius: "16px",
                border: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
            >
              <div className="card-body p-3">
                <div className="d-flex gap-2 flex-wrap">
                  {tabConfig.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        className="btn position-relative"
                        style={{
                          background: isActive ? tab.color : "transparent",
                          color: isActive ? "#fff" : "#666",
                          border: isActive ? "none" : "2px solid #e0e0e0",
                          borderRadius: "10px",
                          padding: "0.75rem 1.5rem",
                          fontWeight: isActive ? "600" : "500",
                          transition: "all 0.3s",
                        }}
                        onClick={() => {
                          setActiveTab(tab.id);
                          if (tab.id === "pending") setNewAppointmentsCount(0);
                        }}
                      >
                        <Icon
                          size={18}
                          className="me-2"
                          style={{ display: "inline" }}
                        />
                        {tab.label}
                        {tab.showNotification &&
                          newAppointmentsCount > 0 &&
                          activeTab !== "pending" && (
                            <span
                              className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                              style={{ fontSize: "0.7rem" }}
                            >
                              {newAppointmentsCount}
                            </span>
                          )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="row mb-4">
          <div className="col-12">
            <div
              className="card"
              style={{
                borderRadius: "16px",
                border: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
            >
              <div className="card-body p-4">
                <div className="row g-3 align-items-end">
                  <div className="col-md-4">
                    <label className="form-label small text-muted fw-semibold">
                      Search MR-Code or patient Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="MR-00001 or Patient Name"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      style={{ borderRadius: "10px", padding: "0.6rem" }}
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label small text-muted fw-semibold">
                      Department
                    </label>
                    <select
                      name="department"
                      className="form-select"
                      value={filters.department}
                      onChange={handleFilterChange}
                      style={{ borderRadius: "10px", padding: "0.6rem" }}
                    >
                      <option value="">All Departments</option>
                      {departments.map((dept) => (
                        <option key={dept._id} value={dept.name}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label small text-muted fw-semibold">
                      Filter by Date
                    </label>
                    <input
                      type="date"
                      name="date"
                      className="form-control"
                      value={filters.date}
                      onChange={handleFilterChange}
                      style={{ borderRadius: "10px", padding: "0.6rem" }}
                    />
                  </div>

                  <div className="col-md-2">
                    <button
                      className="btn btn-outline-secondary w-100"
                      onClick={clearFilters}
                      style={{ borderRadius: "10px", padding: "0.6rem" }}
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="row">
          <div className="col-12">
            <div
              className="card"
              style={{
                borderRadius: "16px",
                border: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
            >
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <Loading />
                  </div>
                ) : (
                  <div
                    className="table-responsive"
                    style={{ maxHeight: "600px", overflowY: "auto" }}
                  >
                    <table
                      className="table table-hover mb-0"
                      style={{ minWidth: "1200px" }}
                    >
                      <thead
                        style={{
                          background: "#f8f9fa",
                          borderBottom: "2px solid #e9ecef",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                        }}
                      >
                        <tr>
                          <th style={{ padding: "1rem", fontWeight: 600 }}>
                            MR-Code
                          </th>
                          <th style={{ padding: "1rem", fontWeight: 600 }}>
                            Patient
                          </th>
                          <th style={{ padding: "1rem", fontWeight: 600 }}>
                            Appt #
                          </th>
                          <th style={{ padding: "1rem", fontWeight: 600 }}>
                            Department
                          </th>
                          <th style={{ padding: "1rem", fontWeight: 600 }}>
                            Doctor
                          </th>
                          <th style={{ padding: "1rem", fontWeight: 600 }}>
                            Date
                          </th>
                          <th style={{ padding: "1rem", fontWeight: 600 }}>
                            Time
                          </th>
                          <th style={{ padding: "1rem", fontWeight: 600 }}>
                            Priority
                          </th>
                          <th style={{ padding: "1rem", fontWeight: 600 }}>
                            Fee
                          </th>
                          <th style={{ padding: "1rem", fontWeight: 600 }}>
                            Status
                          </th>
                          <th style={{ padding: "1rem", fontWeight: 600 }}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAppointments &&
                        filteredAppointments.length > 0 ? (
                          filteredAppointments.map((appt) => (
                            <tr
                              key={appt._id}
                              style={{ borderBottom: "1px solid #f0f0f0" }}
                            >
                              <td style={{ padding: "1rem" }}>
                                <span className="fw-semibold text-primary">
                                  {appt.mrCode || "-"}
                                </span>
                              </td>
                              <td style={{ padding: "1rem" }}>
                                <div className="fw-semibold">{appt.pName}</div>
                                <small className="text-muted">
                                  {appt.contact}
                                </small>
                              </td>
                              <td style={{ padding: "1rem" }}>
                                <span className="badge bg-secondary">
                                  {appt.appointmentNumber || "TBD"}
                                </span>
                              </td>
                              <td style={{ padding: "1rem" }}>
                                {appt.department}
                              </td>
                              <td style={{ padding: "1rem" }}>
                                {appt.doctor ? (
                                  `Dr. ${appt.doctor.name}`
                                ) : (
                                  <span className="text-muted">
                                    Not Assigned
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: "1rem" }}>
                                {appt.date || "-"}
                              </td>
                              <td style={{ padding: "1rem" }}>
                                {formatTime12Hour(appt.time) || "-"}
                              </td>
                              <td style={{ padding: "1rem" }}>
                                {appt.priority && (
                                  <span
                                    className="badge"
                                    style={{
                                      background: getPriorityColor(
                                        appt.priority
                                      ),
                                      color: "white",
                                    }}
                                  >
                                    {appt.priority}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: "1rem" }}>
                                <div className="fw-semibold">
                                  Rs. {appt.payment?.feePaid || 0}
                                </div>
                                <span
                                  className={`badge ${
                                    appt.payment?.status === "paid"
                                      ? "bg-success"
                                      : "bg-warning"
                                  }`}
                                  style={{ fontSize: "0.7rem" }}
                                >
                                  {appt.payment?.status || "unpaid"}
                                </span>
                              </td>
                              <td style={{ padding: "1rem" }}>
                                <span
                                  className="badge"
                                  style={{
                                    background: getStatusBadge(appt.status).bg,
                                    color: getStatusBadge(appt.status).text,
                                    padding: "0.4rem 0.8rem",
                                  }}
                                >
                                  {appt.status}
                                </span>
                              </td>
                              <td style={{ padding: "1rem" }}>
                                <div className="d-flex gap-1">
                                  <button
                                    className="btn btn-sm btn-outline-info"
                                    onClick={() => handleViewClick(appt)}
                                    title="View Details"
                                    style={{ borderRadius: "6px" }}
                                  >
                                    <Eye size={16} />
                                  </button>

                                  {appt.status === "pending" && (
                                    <>
                                      <button
                                        className="btn btn-sm btn-outline-success"
                                        onClick={() => handleAssignClick(appt)}
                                        title="Assign Doctor"
                                        style={{ borderRadius: "6px" }}
                                      >
                                        <UserCheck size={16} />
                                      </button>
                                      <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleRejectClick(appt)}
                                        title="Reject"
                                        style={{ borderRadius: "6px" }}
                                      >
                                        <XCircle size={16} />
                                      </button>
                                    </>
                                  )}

                                  {appt.status === "confirmed" && (
                                    <>
                                      {appt.status === "confirmed" && (
                                        <>
                                          <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() =>
                                              printWalkInReceipt(appt)
                                            }
                                            title={
                                              appt.payment?.status === "paid"
                                                ? "Print Appointment Slip"
                                                : "Payment required to print slip"
                                            }
                                            style={{
                                              borderRadius: "6px",
                                              opacity:
                                                appt.payment?.status === "paid"
                                                  ? 1
                                                  : 0.5,
                                              cursor:
                                                appt.payment?.status === "paid"
                                                  ? "pointer"
                                                  : "not-allowed",
                                            }}
                                            disabled={
                                              appt.payment?.status !== "paid"
                                            }
                                          >
                                            <Printer size={16} />
                                          </button>

                                          {/* Show Record Payment button only if payment is NOT paid */}
                                          {appt.payment?.status !== "paid" && (
                                            <button
                                              className="btn btn-sm btn-outline-warning"
                                              onClick={() =>
                                                handlePaymentClick(appt)
                                              }
                                              title="Record Payment"
                                              style={{ borderRadius: "6px" }}
                                            >
                                              <DollarSign size={16} />
                                            </button>
                                          )}

                                          {/* Show Print Payment Receipt button only if payment is paid */}
                                          {appt.payment?.status === "paid" && (
                                            <button
                                              className="btn btn-sm btn-outline-success"
                                              onClick={() => printPayment(appt)}
                                              title="Print Payment Receipt"
                                              style={{ borderRadius: "6px" }}
                                            >
                                              <Receipt size={16} />
                                            </button>
                                          )}
                                        </>
                                      )}
                                      {/* <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => handleComplete(appt)}
                                        title="Complete"
                                        style={{ borderRadius: "6px" }}
                                      >
                                        <CheckCircle size={16} />
                                      </button> */}
                                    </>
                                  )}

                                  {appt.status === "completed" && (
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => printWalkInReceipt(appt)}
                                      title="Print Receipt"
                                      style={{ borderRadius: "6px" }}
                                    >
                                      <Printer size={16} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="11" className="text-center py-5">
                              <FileText size={64} color="#ddd" />
                              <p className="text-muted mt-3 mb-0">
                                No appointments found
                              </p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AssignDoctorModal
        show={showAssignModal}
        onHide={() => setShowAssignModal(false)}
        doctors={doctors || []}
        assignData={assignData}
        setAssignData={setAssignData}
        onAssign={handleAssignModalNext}
        loading={loading}
      />

      <VitalsModal
        show={showVitalsModal}
        onHide={() => {
          setShowVitalsModal(false);
          setShowAssignModal(true); // Allow going back to assignment
        }}
        vitalsData={vitalsData}
        setVitalsData={setVitalsData}
        onSubmit={handleVitalsSubmit}
        loading={loading}
        patientInfo={selectedAppt}
      />

      <RejectAppointmentModal
        show={showRejectModal}
        onHide={() => setShowRejectModal(false)}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        onReject={handleRejectSubmit}
        loading={loading}
      />

      <PaymentModal
        show={showPaymentModal}
        onHide={() => setShowPaymentModal(false)}
        paymentData={paymentData}
        setPaymentData={setPaymentData}
        onSubmit={handlePaymentSubmit}
        loading={loading}
      />

      <ViewAppointmentModal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        appointment={selectedAppt}
        onPrint={printWalkInReceipt}
        getStatusBadge={getStatusBadge}
      />
    </div>
  );
};

export default ManageAppointmentsReceptionist;
